import type { AiComponentDto } from '@terra/shared-contracts-ai-applications';
import {
  McpAssetAccessScopeSchema,
  McpIdentityAccessEntrySchema,
  McpIdentityRosterEntrySchema,
  McpServerAuthMetadataSchema,
  type McpAssetAccessScopeDto,
  type McpIdentityAccessEntryDto,
  type McpIdentityRosterEntryDto,
  type McpServerAuthMetadataDto,
} from '@terra/shared-contracts-ai-applications';
import { JsonValueSchema } from '@terra/shared-schemas-orchestrator';
import { z } from 'zod';

type JsonValue = z.infer<typeof JsonValueSchema>;

const MetadataRecordSchema = z.record(z.string(), JsonValueSchema);

export function parseAiComponentMetadata(
  metadata: AiComponentDto['metadata'],
): Record<string, JsonValue> {
  const parsedMetadata = MetadataRecordSchema.safeParse(metadata);

  return parsedMetadata.success ? parsedMetadata.data : {};
}

export function getAiComponentMetadataString({
  metadata,
  field,
}: {
  metadata: AiComponentDto['metadata'];
  field: string;
}): string | null {
  const parsedMetadata = MetadataRecordSchema.safeParse(metadata);

  if (!parsedMetadata.success) {
    return null;
  }

  const value = parsedMetadata.data[field];

  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : null;
}

export type AiComponentSystemPromptSource =
  | 'source_code'
  | 'runtime_verbatim'
  | 'business_context'
  | 'discovery';

const SYSTEM_PROMPT_SOURCE_VALUES = new Set<AiComponentSystemPromptSource>([
  'source_code',
  'runtime_verbatim',
  'business_context',
  'discovery',
]);

export function getAiComponentSystemPromptSource({
  metadata,
}: {
  metadata: AiComponentDto['metadata'];
}): AiComponentSystemPromptSource | null {
  const systemPromptSource = getAiComponentMetadataString({
    metadata,
    field: 'systemPromptSource',
  });

  if (
    systemPromptSource &&
    SYSTEM_PROMPT_SOURCE_VALUES.has(
      systemPromptSource as AiComponentSystemPromptSource,
    )
  ) {
    return systemPromptSource as AiComponentSystemPromptSource;
  }

  const componentSource = getAiComponentMetadataString({
    metadata,
    field: 'source',
  });

  if (componentSource === 'source_code') {
    return 'source_code';
  }

  return null;
}

export function getAiComponentInputSchema({
  metadata,
  runtimeOverlay,
}: {
  metadata: AiComponentDto['metadata'];
  runtimeOverlay: AiComponentDto['runtimeOverlay'];
}): JsonValue | null {
  const metadataRecord = parseAiComponentMetadata(metadata);
  const declared = metadataRecord.declared;

  if (declared && typeof declared === 'object' && declared !== null) {
    const declaredRecord = MetadataRecordSchema.safeParse(declared);

    if (declaredRecord.success) {
      const inputSchema = declaredRecord.data.inputSchema;
      const parsedInputSchema = JsonValueSchema.safeParse(inputSchema);

      if (parsedInputSchema.success) {
        return parsedInputSchema.data;
      }
    }
  }

  for (const source of [metadata, runtimeOverlay]) {
    const parsedSource = MetadataRecordSchema.safeParse(source);

    if (!parsedSource.success) {
      continue;
    }

    const inputSchema = parsedSource.data.inputSchema;

    if (inputSchema === undefined) {
      continue;
    }

    const parsedInputSchema = JsonValueSchema.safeParse(inputSchema);

    if (parsedInputSchema.success) {
      return parsedInputSchema.data;
    }
  }

  return null;
}

const MCP_ASSET_COMPONENT_TYPES = new Set([
  'MCP_TOOL',
  'MCP_PROMPT',
  'MCP_RESOURCE',
  'MCP_PROTOCOL',
]);

export function isMcpAssetComponentType(type: AiComponentDto['type']): boolean {
  return MCP_ASSET_COMPONENT_TYPES.has(type);
}

export function getMcpComponentAccessScope({
  metadata,
}: {
  metadata: AiComponentDto['metadata'];
}): McpAssetAccessScopeDto | null {
  const identityAccess = getMcpComponentIdentityAccess({ metadata });

  if (identityAccess.length > 0) {
    return identityAccess.every((entry) => entry.visible ?? entry.accessible)
      ? 'ALL'
      : 'SOME';
  }

  const parsedMetadata = parseAiComponentMetadata(metadata);
  const parsedScope = McpAssetAccessScopeSchema.safeParse(
    parsedMetadata.accessScope,
  );

  return parsedScope.success ? parsedScope.data : null;
}

export function getMcpComponentIdentityAccess({
  metadata,
}: {
  metadata: AiComponentDto['metadata'];
}): McpIdentityAccessEntryDto[] {
  const parsedMetadata = parseAiComponentMetadata(metadata);
  const parsedIdentityAccess = z
    .array(McpIdentityAccessEntrySchema)
    .safeParse(parsedMetadata.identityAccess);

  return parsedIdentityAccess.success ? parsedIdentityAccess.data : [];
}

export function getMcpComponentDeniedIdentityAccess({
  metadata,
}: {
  metadata: AiComponentDto['metadata'];
}): McpIdentityAccessEntryDto[] {
  return getMcpComponentIdentityAccess({ metadata }).filter(
    (entry) => !(entry.visible ?? entry.accessible),
  );
}

export type McpIdentityAccessSummary = {
  accessScope: McpAssetAccessScopeDto;
  visibleCount: number;
  totalCount: number;
  visibleLabels: string[];
  deniedCount: number;
  deniedLabels: string[];
};

export type McpServerIdentityCapability = {
  identityId: string;
  label: string;
  assetNames: string[];
  accessDenied: boolean;
};

export function getMcpComponentIdentityAccessSummary({
  metadata,
}: {
  metadata: AiComponentDto['metadata'];
}): McpIdentityAccessSummary | null {
  const identityAccess = getMcpComponentIdentityAccess({ metadata });

  if (identityAccess.length === 0) {
    const accessScope = getMcpComponentAccessScope({ metadata });

    return accessScope
      ? {
          accessScope,
          visibleCount: 0,
          totalCount: 0,
          visibleLabels: [],
          deniedCount: 0,
          deniedLabels: [],
        }
      : null;
  }

  const visibleEntries = identityAccess.filter(
    (entry) => entry.visible ?? entry.accessible,
  );
  const deniedEntries = identityAccess.filter(
    (entry) => !(entry.visible ?? entry.accessible),
  );
  const accessScope: McpAssetAccessScopeDto =
    visibleEntries.length === identityAccess.length ? 'ALL' : 'SOME';

  return {
    accessScope,
    visibleCount: visibleEntries.length,
    totalCount: identityAccess.length,
    visibleLabels: visibleEntries.map((entry) => entry.label),
    deniedCount: deniedEntries.length,
    deniedLabels: deniedEntries.map((entry) => entry.label),
  };
}

export type McpIdentityAccessLabels = {
  accessDenied: string;
  call: string;
};

export function resolveMcpIdentityAccessPresentation({
  entry,
  labels,
}: {
  entry: McpIdentityAccessEntryDto;
  labels: McpIdentityAccessLabels;
}): {
  label: string;
  colorPalette: 'green' | 'orange' | 'gray' | 'red';
} {
  const visible = entry.visible ?? entry.accessible;

  if (!visible) {
    return { label: labels.accessDenied, colorPalette: 'gray' };
  }

  switch (entry.callOutcome) {
    case 'denied':
    case 'insufficient_scope':
    case 'error':
      return { label: labels.accessDenied, colorPalette: 'gray' };
    case 'success':
    case 'untested':
    default:
      return { label: labels.call, colorPalette: 'green' };
  }
}

export function formatMcpIdentityAccessTooltipContent({
  metadata,
  labels,
}: {
  metadata: AiComponentDto['metadata'];
  labels: McpIdentityAccessLabels;
}): string | undefined {
  const identityAccess = getMcpComponentIdentityAccess({ metadata });

  if (identityAccess.length === 0) {
    return undefined;
  }

  return identityAccess
    .map((entry) => {
      const { label } = resolveMcpIdentityAccessPresentation({ entry, labels });
      const missingScopes = entry.missingScopes ?? [];
      const scopeSuffix =
        missingScopes.length > 0 ? ` — ${missingScopes.join(', ')}` : '';

      return `${entry.label}: ${label}${scopeSuffix}`;
    })
    .join('\n');
}

type McpServerIdentitySource = {
  identityId: string;
  label: string;
  connectionSuccess: boolean;
};

function collectMcpServerIdentitySources({
  serverComponent,
  assetComponents,
}: {
  serverComponent: AiComponentDto;
  assetComponents: AiComponentDto[];
}): McpServerIdentitySource[] {
  const roster = getMcpServerIdentityRoster({
    metadata: serverComponent.metadata,
  });

  if (roster.length > 0) {
    return roster.map((entry) => ({
      identityId: entry.identityId,
      label: entry.label,
      connectionSuccess: entry.connectionSuccess,
    }));
  }

  const assets = assetComponents.filter((component) =>
    isMcpAssetComponentType(component.type),
  );
  const identitiesById = new Map<string, McpServerIdentitySource>();

  for (const asset of assets) {
    for (const entry of getMcpComponentIdentityAccess({
      metadata: asset.metadata,
    })) {
      if (!identitiesById.has(entry.identityId)) {
        identitiesById.set(entry.identityId, {
          identityId: entry.identityId,
          label: entry.label,
          connectionSuccess: true,
        });
      }
    }
  }

  return [...identitiesById.values()].sort((left, right) =>
    left.label.localeCompare(right.label),
  );
}

export function getMcpServerIdentityCapabilities({
  serverComponent,
  assetComponents,
}: {
  serverComponent: AiComponentDto;
  assetComponents: AiComponentDto[];
}): McpServerIdentityCapability[] {
  const identitySources = collectMcpServerIdentitySources({
    serverComponent,
    assetComponents,
  });

  if (identitySources.length === 0) {
    return [];
  }

  const assets = assetComponents.filter((component) =>
    isMcpAssetComponentType(component.type),
  );

  return identitySources.map((entry) => {
    const identityAccessByAsset = assets.map((asset) => ({
      asset,
      identityEntry: getMcpComponentIdentityAccess({
        metadata: asset.metadata,
      }).find((access) => access.identityId === entry.identityId),
    }));
    const hasIdentityAccessEvidence = identityAccessByAsset.some(
      ({ identityEntry }) => identityEntry !== undefined,
    );
    const assetNames = identityAccessByAsset
      .filter(
        ({ identityEntry }) =>
          identityEntry !== undefined &&
          (identityEntry.visible ?? identityEntry.accessible),
      )
      .map(({ asset }) => asset.name)
      .sort((left, right) => left.localeCompare(right));

    return {
      identityId: entry.identityId,
      label: entry.label,
      assetNames,
      accessDenied:
        !entry.connectionSuccess ||
        (hasIdentityAccessEvidence && assetNames.length === 0),
    };
  });
}

export function getMcpJsonMetadataBlock({
  metadata,
  field,
}: {
  metadata: AiComponentDto['metadata'];
  field: 'declared' | 'observed';
}): JsonValue | null {
  const metadataRecord = parseAiComponentMetadata(metadata);
  const block = metadataRecord[field];
  const parsedBlock = JsonValueSchema.safeParse(block);

  return parsedBlock.success ? parsedBlock.data : null;
}

export function getMcpServerAuthMetadata({
  metadata,
}: {
  metadata: AiComponentDto['metadata'];
}): McpServerAuthMetadataDto | null {
  const parsedMetadata = parseAiComponentMetadata(metadata);
  const parsedAuth = McpServerAuthMetadataSchema.safeParse(parsedMetadata.auth);

  return parsedAuth.success ? parsedAuth.data : null;
}

export function getMcpServerIdentityRoster({
  metadata,
}: {
  metadata: AiComponentDto['metadata'];
}): McpIdentityRosterEntryDto[] {
  const parsedMetadata = parseAiComponentMetadata(metadata);
  const parsedRoster = z
    .array(McpIdentityRosterEntrySchema)
    .safeParse(parsedMetadata.identityRoster);

  return parsedRoster.success ? parsedRoster.data : [];
}

export function hasMcpServerAuthSummary({
  component,
}: {
  component: AiComponentDto;
}): boolean {
  if (component.type !== 'MCP_SERVER') {
    return false;
  }

  const auth = getMcpServerAuthMetadata({ metadata: component.metadata });

  if (auth) {
    return true;
  }

  return (
    getMcpServerIdentityRoster({ metadata: component.metadata }).length > 0
  );
}
